import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDownCircle, PlusCircle, RefreshCw, Search, WalletCards } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { StatusPill } from "./AdminTransactions";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";
import { VerifiedName } from "../../components/VerifiedBadge";

export default function AdminWallet() {
  const [data, setData] = useState({ summary: {}, users: [], recent: [] });
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState("");
  const [direction, setDirection] = useState("credit");
  const [amount, setAmount] = useState("10");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/wallet");
    setData(data);
    if (!selected && data.users?.[0]) setSelected(data.users[0].id);
  }, [selected]);

  useEffect(() => { load().catch(() => {}); }, [load]);
  useAdminAutoRefresh(() => load());

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.users || [];
    return (data.users || []).filter((user) =>
      [user.username, user.email, user.phone].filter(Boolean).some((value) => String(value).toLowerCase().includes(q)),
    );
  }, [data.users, query]);

  const selectedUser = (data.users || []).find((user) => user.id === selected);

  const submit = async (e) => {
    e.preventDefault();
    const dollars = Number(amount);
    if (!selectedUser) {
      toast.error("Select a customer first");
      return;
    }
    if (!Number.isFinite(dollars) || dollars <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setLoading(true);
    try {
      await api.post("/admin/wallet/adjust", {
        user_id: selectedUser.id,
        direction,
        amount: Math.round(dollars * 1000),
        note,
      });
      toast.success(direction === "credit" ? "Wallet credited" : "Wallet debited");
      setNote("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 md:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Wallet</h1>
          <p className="mt-1 text-sm text-slate-500">Customer balances, manual credits, and payout control.</p>
        </div>
        <button onClick={() => load().catch(() => {})} className="rounded-md border border-white/10 p-2 hover:bg-white/5">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      <div className="mb-6 grid gap-3 md:grid-cols-4">
        <Stat label="Player balance" value={money(data.summary?.total_coins)} />
        <Stat label="Players" value={(data.summary?.players || 0).toLocaleString()} />
        <Stat label="Phone verified" value={(data.summary?.phone_verified || 0).toLocaleString()} />
        <Stat label="Pending payout" value={(data.summary?.pending_payouts || 0).toLocaleString()} />
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <form onSubmit={submit} className="rounded-xl border border-white/10 bg-[#121214] p-5">
          <div className="mb-5 flex items-center gap-2">
            <WalletCards className="h-5 w-5 text-[#2F6FA5]" />
            <h2 className="text-lg font-semibold">Manual adjustment</h2>
          </div>

          <div className="space-y-4">
            <label className="block text-sm">
              <span className="mb-1 block text-xs uppercase tracking-wider text-slate-500">Customer</span>
              <select value={selected} onChange={(e) => setSelected(e.target.value)} className={inputClass}>
                {(data.users || []).map((user) => (
                  <option key={user.id} value={user.id}>{user.username} - {user.email}</option>
                ))}
              </select>
            </label>

            {selectedUser && (
              <div className="rounded-lg border border-white/10 bg-black/20 p-3 text-sm">
                <div className="font-medium">{selectedUser.username}</div>
                <div className="text-slate-500">{selectedUser.email}</div>
                <div className="mt-2 text-xl font-semibold tabular-nums text-[#2F6FA5]">{money(selectedUser.coins)}</div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setDirection("credit")}
                className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold ${direction === "credit" ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
                <PlusCircle className="h-4 w-4" /> Credit
              </button>
              <button type="button" onClick={() => setDirection("debit")}
                className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold ${direction === "debit" ? "border-amber-500/50 bg-amber-500/15 text-amber-300" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
                <ArrowDownCircle className="h-4 w-4" /> Debit
              </button>
            </div>

            <label className="block text-sm">
              <span className="mb-1 block text-xs uppercase tracking-wider text-slate-500">Amount ($)</span>
              <input type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} />
            </label>

            <label className="block text-sm">
              <span className="mb-1 block text-xs uppercase tracking-wider text-slate-500">Admin note</span>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={inputClass} placeholder="CashApp received, payout correction, bonus credit..." />
            </label>

            <button disabled={loading || !selectedUser} className="w-full rounded-md bg-[#2F6FA5] px-4 py-3 font-bold text-white hover:bg-[#4E91C8] disabled:opacity-50">
              {loading ? "Saving..." : direction === "credit" ? "Credit wallet" : "Debit wallet"}
            </button>
          </div>
        </form>

        <section className="rounded-xl border border-white/10 bg-[#121214]">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 p-4">
            <h2 className="text-lg font-semibold">Customer wallets</h2>
            <label className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} className="rounded-md border border-white/10 bg-black/20 py-2 pl-9 pr-3 text-sm outline-none focus:border-[#2F6FA5]" placeholder="Search customer..." />
            </label>
          </div>
          <div className="max-h-[34rem] overflow-auto">
            <table className="min-w-[760px] w-full text-sm">
              <thead className="sticky top-0 bg-white/5 text-left text-xs uppercase tracking-wider text-slate-400 backdrop-blur">
                <tr>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3 text-right">Balance</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="border-t border-white/5">
                    <td className="px-4 py-3">
                      <div className="font-medium"><VerifiedName name={user.username} user={user} /></div>
                      <div className="text-xs text-slate-500">{user.email}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums">{money(user.coins)}</td>
                    <td className="px-4 py-3 text-slate-500">{user.phone_verified ? user.phone || "Verified" : "Not verified"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setSelected(user.id)} className="rounded-md border border-white/10 px-3 py-1.5 text-xs hover:bg-white/5">Select</button>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-slate-500">No customers found.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="mt-6 rounded-xl border border-white/10 bg-[#121214] p-5">
        <h2 className="mb-4 text-lg font-semibold">Recent wallet activity</h2>
        <div className="space-y-2">
          {(data.recent || []).map((row) => (
            <div key={row.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/5 bg-black/20 p-3 text-sm">
              <div className="min-w-0">
                <div className="font-medium">
                  <VerifiedName name={row.username || "-"} user={row} /> <span className="text-slate-500">({row.email || "-"})</span>
                </div>
                <div className="truncate text-xs text-slate-500">{(row.type || "").replace(/_/g, " ")} - {row.note || row.admin_note || "No note"}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold tabular-nums">{money(row.amount)}</span>
                <StatusPill status={row.status} />
              </div>
            </div>
          ))}
          {(data.recent || []).length === 0 && <div className="py-8 text-center text-sm text-slate-500">No wallet activity yet.</div>}
        </div>
      </section>
    </div>
  );
}

const inputClass = "w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-sm text-slate-100 outline-none focus:border-[#2F6FA5]";

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#121214] p-4">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-100">{value}</div>
    </div>
  );
}

function money(coins = 0) {
  return `$${((coins || 0) / 1000).toFixed(2)}`;
}
